import { useGetPersonalInfo } from '@/hooks/api';
import { Button, Sheet, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type ReactNode } from 'react';
import { useNavbarContext } from '../../context/navbar';
import {
  backLocation,
  type FlowData,
  getIconForFlow,
  hasBack,
  ServiceFlowPage,
  ServicesContextProvider,
  useServicesContext,
} from './context';
import { ServicesNavSheetContent } from './services';
import { ArrowLeft } from '@nosinovacao/fe-icons';
import { ServicesNavSheetAppsContent } from './app';
import { ServicesNavSheetAddServicesContent } from './add-services';
import { ServicesNavSheetHolderInputContent } from './holder-input';
import { ServicesNavSheetContactSelectionContent } from './contact-selection';
import { ServicesNavSheetCodeInputContent } from './code-input';
import { ServicesNavSheetSuccessContent } from './success';

export function ServicesFlow() {
  const { data } = useGetPersonalInfo();

  if (data?.Type && data.Type !== 'Guest') return <></>;

  return (
    <ServicesContextProvider>
      <ServicesSheet>
        <ServiceFlowPage flow="main">
          <ServicesNavSheetContent />
        </ServiceFlowPage>
        <ServiceFlowPage flow="apps">
          <ServicesNavSheetAppsContent />
        </ServiceFlowPage>
        <ServiceFlowPage flow="add-services">
          <ServicesNavSheetAddServicesContent />
        </ServiceFlowPage>
        <ServiceFlowPage flow="holder-input">
          <ServicesNavSheetHolderInputContent />
        </ServiceFlowPage>
        <ServiceFlowPage flow="contact-selection">
          <ServicesNavSheetContactSelectionContent />
        </ServiceFlowPage>
        <ServiceFlowPage flow="code-input">
          <ServicesNavSheetCodeInputContent />
        </ServiceFlowPage>
        <ServiceFlowPage flow="success">
          <ServicesNavSheetSuccessContent />
        </ServiceFlowPage>
      </ServicesSheet>
    </ServicesContextProvider>
  );
}

const translationsForFlows: Record<string, string | ((flowData: FlowData) => string)> = {
  apps: 'Apps.Title',
  'add-services': 'Services.AddServices.Title',
  'holder-input': (flow) => `Services.AddServices.HolderInput.Title.${flow.associationType}`,
  'contact-selection': 'Services.AddServices.ContactSelection.Title',
  'code-input': 'Services.AddServices.CodeInput.Title',
  success: 'Services.AddServices.Success.Title',
};

const subtitleTranslationsForFlows: Record<string, string | ((flowData: FlowData) => string)> = {
  'add-services': 'Services.AddServices.Subtitle',
  success: 'Services.AddServices.Success.Subtitle',
  'code-input': (flow) => {
    let key = `Services.AddServices.CodeInput.Subtitle.${flow.associationType}`;
    if (flow.associationType === 'Nif' || flow.associationType === 'Passport') {
      const contactSelectionType = flow.contactSelectionContacts.find(
        (ct) => ct.Id === flow.contactId,
      )!.Type;
      key += `.${contactSelectionType}`;
    }
    return key;
  },
};

function ServicesSheet({ children }: Readonly<{ children: ReactNode }>) {
  const { t } = useI18n();
  const { activeSection: sectionName, setActiveSection: setSectionName } = useNavbarContext();
  const { flow, setFlow, flowData } = useServicesContext();
  const titleKey = translationsForFlows[flow];
  const subtitleKey = subtitleTranslationsForFlows[flow];
  return (
    <Sheet
      title={titleKey ? t(typeof titleKey === 'string' ? titleKey : titleKey(flowData)) : undefined}
      subtitle={
        subtitleKey
          ? t(typeof subtitleKey === 'string' ? subtitleKey : subtitleKey(flowData), {
              contact: flowData.selectedContact,
            })
          : undefined
      }
      back={
        hasBack(flow) ? (
          <Button
            skin="light"
            variant="tertiary"
            onClick={() => setFlow(backLocation(flow, flowData), flowData)}
            data-tid="button-back"
          >
            <ArrowLeft width={20} height={20} />
            {t('Common.Back')}
          </Button>
        ) : null
      }
      icon={getIconForFlow(flow)}
      skin={'light'}
      trigger={undefined}
      open={sectionName === 'services'}
      overlayClassName="z-[60]"
      className="z-[60]"
      onOpenChange={(o) => {
        if (o) return;
        setSectionName('main');
        setFlow('main');
      }}
    >
      {children}
    </Sheet>
  );
}
